import {Modal} from 'antd'
import {
    report_getById_Api,
    report_manageApproveReport_Api,
    report_manageDeleteReport_Api,
    report_manageRejectReport_Api,
    report_manageResetReport_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo, useState} from 'react'
import {BsCheckCircle, BsClock, BsReplyFill, BsTrash, BsXCircle,} from 'react-icons/bs'
import {FaMinusCircle} from 'react-icons/fa'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {getResponseItem, NullAsDefault, renderIf} from 'views/Shared'
import ApproveReportModal from './components/ApproveReportModal'
import RejectReportModal from './components/RejectReportModal'

const ErrorReportContext = React.createContext({})

export const ErrorReportStatus = Object.freeze({
  NORMAL: {
    value: -1,
    label: 'pending',
    icon: <BsClock size={17} className="text-blue-500"/>,
    component: (
        <Translate>
          {(t) => (
              <span className="font-medium text-xs text-blue-500 italic">
                {t('pending')}
              </span>
          )}
        </Translate>
    ),
  },
  APPROVED: {
    value: 9,
    label: 'closed',
    icon: <BsCheckCircle size={17} className="text-green-500"/>,
    component: (
        <Translate>
          {(t) => (
              <span className="font-medium text-xs text-green-500 italic">
                {t('closed')}
              </span>
          )}
        </Translate>
    ),
  },
  REJECTED: {
    value: -9,
    label: 'rejected',
    icon: <BsXCircle size={17} className="text-red-500"/>,
    component: (
        <Translate>
          {(t) => (
              <span className="font-medium text-xs text-orange-500 italic">
                {t('rejected')}
              </span>
          )}
        </Translate>
    ),
  },
})

export const ErrorReportActionTypes = Object.freeze({
  REFRESH: {
    value: 'refresh',
  },
  CANCEL: {
    value: 'cancel',
    label: 'cancel report',
    Icon: FaMinusCircle,
    className: 'text-red-300 hover:text-red-600',
  },
  APPROVE: {
    value: 'approve',
    label: 'approve',
    Icon: BsCheckCircle,
    className: 'text-green-300 hover:text-green-600',
  },
  REJECT: {
    value: 'reject',
    label: 'reject',
    Icon: BsXCircle,
    className: 'text-orange-300 hover:text-orange-600',
  },
  RESET: {
    value: 'reset',
    label: 'undo',
    Icon: BsReplyFill,
    className: 'text-blue-500 hover:text-blue-700',
    confirm: {
      title: 'undo',
      message: 'are you sure',
      apiInfo: report_manageResetReport_Api,
      withItem: (item) => [{}, { id: getId(item) }],
      notify: (callback = NullAsDefault) => {
        callback(NotificationActionTypes.INFORMATION)
      },
    },
  },
  DELETE: {
    value: 'delete',
    label: 'delete',
    Icon: BsTrash,
    className: 'text-red-300 hover:text-red-600',
    confirm: {
      title: 'delete report',
      message: 'are you sure delete this report',
      apiInfo: report_manageDeleteReport_Api,
      withItem: (item) => [{}, { id: getId(item) }],
      notify: (callback = NullAsDefault) => {
        callback(NotificationActionTypes.DELETE)
      },
    },
  },
})

export function getErrorReportActions(status) {
  switch (status) {
    case -1:
      return [
        ErrorReportActionTypes.APPROVE,
        ErrorReportActionTypes.REJECT,
        ErrorReportActionTypes.DELETE,
      ]
    case 9:
      return [ErrorReportActionTypes.RESET, ErrorReportActionTypes.DELETE]
    case -9:
      return [ErrorReportActionTypes.RESET, ErrorReportActionTypes.DELETE]
    default:
      break
  }
}

export const FetchErrorReport = ({ id, children }) =>
  renderIf(
    id,
    <Async
      {...{
        apiInfo: report_getById_Api,
        query: bindQueryParam({ id }),
      }}>
      {(data) => {
        const item = getResponseItem(data?.response)
        if (_.isFunction(children)) {
          return children(item, data)
        }
        return children ?? null
      }}
    </Async>
  )

export const ErrorReportProvider = ({
  owner_id,
  owner_type,
  children,
  ...props
}) => {
  const t = useTranslate()

  const [spinning, setSpinning] = useState()

  const [refreshToken, setRefreshToken] = useState({
    type: undefined,
    value: undefined,
  })

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const onCancel = () => {
    setAction({})
  }

  const reset = () => {
    setRefreshToken(Date.now())
  }

  const approveAsync = useAsyncAction({
    apiInfo: report_manageApproveReport_Api,
    onError: (e, o) => {
      setSpinning(false)
      notifyOnError(t)(e, o)
    },
    onSuccess: () => {
      setSpinning(false)
      successNotify(NotificationActionTypes.INFORMATION, t)
      onCancel()
      reset()
    },
  })

  const rejectAsync = useAsyncAction({
    apiInfo: report_manageRejectReport_Api,
    onError: (e, o) => {
      setSpinning(false)
      notifyOnError(t)(e, o)
    },
    onSuccess: () => {
      setSpinning(false)
      successNotify(NotificationActionTypes.INFORMATION, t)
      onCancel()
      reset()
    },
  })

  const handleAction = (type, value) => {
    switch (type) {
      case ErrorReportActionTypes.REFRESH.value:
        reset()
        break
      case ErrorReportActionTypes.APPROVE.value:
      case ErrorReportActionTypes.REJECT.value:
        setAction({
          type,
          value,
        })
        break
      case ErrorReportActionTypes.RESET.value:
        break
      case ErrorReportActionTypes.DELETE.value:
        break
      case ErrorReportActionTypes.CANCEL.value:
        break
      default:
        break
    }
  }

  const modal = useMemo(() => {
    let modal_params = {}
    let Component = NullAsDefault
    const report_id = getId(action?.value)
    switch (action?.type) {
      case ErrorReportActionTypes.APPROVE.value:
        modal_params = {
          title: (
            <div className="text-center font-bold text-color-000 uppercase tracking-wide">
              {t('approve report')}
            </div>
          ),
        }
        Component = (props) => (
          <FetchErrorReport id={report_id}>
            {(item, { isLoading }) => (
              <ApproveReportModal
                {...props}
                item={item}
                isLoading={isLoading}
                type={action?.type}
                onSubmit={(values) => {
                  setSpinning(true)
                  const approve = approveAsync?.handleAsyncAction ?? NullAsDefault
                  approve(values, { id: report_id })
                }}
              />
            )}
          </FetchErrorReport>
        )
        break
      case ErrorReportActionTypes.REJECT.value:
        modal_params = {
          title: (
            <div className="text-center font-bold text-color-000 uppercase tracking-wide">
              {t('reject report')}
            </div>
          ),
        }
        Component = (props) => (
          <FetchErrorReport id={report_id}>
            {(item, { isLoading }) => (
              <RejectReportModal
                {...props}
                item={item}
                isLoading={isLoading}
                type={action?.type}
                onSubmit={(values) => {
                  setSpinning(true)
                  const reject = rejectAsync?.handleAsyncAction ?? NullAsDefault
                  reject(values, { id: report_id })
                }}
              />
            )}
          </FetchErrorReport>
        )
        break
      default:
        break
    }
    return (
      <Modal
        className="custom-modal"
        footer={null}
        onCancel={onCancel}
        destroyOnHidden={true}
        open={action?.type}
        {...modal_params}>
        <Component {...{ onCancel }} />
      </Modal>
    )
  }, [action])

  return (
    <ErrorReportContext.Provider
      value={{
        ...props,
        onCancel,
        owner_id,
        owner_type,
        action,
        modal,
        spinning,
        setSpinning,
        setAction,
        handleAction,
        refreshToken,
        setRefreshToken,
        rejectAsync,
        approveAsync,
      }}>
      {children}
    </ErrorReportContext.Provider>
  )
}

export default ErrorReportContext
